import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from '../../store/slices/authSlice';
import { closeModal } from '../../store/slices/uiSlice';
import { FaEnvelope } from 'react-icons/fa';
import toast from 'react-hot-toast';

const ForgotPasswordForm = ({ onBackToLogin, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const validationErrors = {};
    
    if (!email.trim()) {
      validationErrors.email = 'Please enter an email address';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      validationErrors.email = 'Please enter a valid email address';
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setErrors({});
    
    try {
      const result = await dispatch(forgotPassword({ email })).unwrap();
      
      // If we get a token in the response, redirect directly to reset password page
      if (result.data && result.data.token) {
        const resetUrl = `/reset-password?token=${result.data.token}&email=${encodeURIComponent(result.data.email)}`;
        toast.success('Redirecting to reset password...');
        
        // Close modal using both methods to ensure it closes
        onClose();
        dispatch(closeModal('forgotPassword'));
        
        // Navigate after a short delay to ensure modal closes
        setTimeout(() => {
          navigate(resetUrl);
        }, 100);
      } else {
        toast.success('Password reset link sent to your email');
        onClose();
        dispatch(closeModal('forgotPassword'));
      }
    } catch (err) {
      toast.error(err || 'Failed to send reset email');
    }
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <h2 className="text-2xl font-bold text-jet-gray-900 mb-2 text-center">Forget Password ?</h2>
      <p className="text-jet-gray-600 text-sm mb-6 text-center leading-relaxed">
        Enter your account's registered email and we will send you instructions to reset your password.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-group">
          <div className="relative">
            <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-jet-gray-400" size={18} />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ textTransform: 'uppercase' }}
              className="w-full pl-10 pr-4 py-3 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
            />
          </div>
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
        
        <button 
          type="submit" 
          disabled={loading}
          className="w-full bg-jet-yellow text-jet-gray-900 border-0 py-3 px-6 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'SENDING...' : 'SUBMIT'}
        </button>
        
        <button type="button" onClick={onBackToLogin} className="w-full text-jet-gray-600 text-sm hover:text-jet-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-2 mt-4">
          Back to Login
        </button>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;